import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import UserModal from './UserModal';
import NotificationToast from './NotificationToast';
import './UserManagement.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Helper function to normalize IDs
const normalizeId = (id) => {
  if (!id) return '';
  if (typeof id === 'object' && id._id) return String(id._id);
  return String(id);
};

const ClientManagement = ({ onUpdate }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [fixedRole, setFixedRole] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [managers, setManagers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [expandedClient, setExpandedClient] = useState(null);
  const [expandedManager, setExpandedManager] = useState(null);
  const [managerEmployees, setManagerEmployees] = useState({});
  const [notification, setNotification] = useState('');
  const notificationTimeoutRef = useRef(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/users`);
      const clientsList = response.data.filter(user => user.role === 'client');
      setClients(clientsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setLoading(false);
    }
  };

  const fetchManagersAndEmployees = async (clientId) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/users`);
      const allUsers = response.data;
      const clientIdStr = normalizeId(clientId);
      const managersList = allUsers.filter(user => {
        const userId = normalizeId(user.clientId);
        return userId === clientIdStr && user.role === 'manager';
      });
      setManagers(managersList);
      setEmployees([]);
    } catch (error) {
      console.error('Error fetching managers/employees:', error);
    }
  };

  const fetchEmployeesForManager = async (managerId) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/users`);
      const allUsers = response.data;
      const managerIdStr = normalizeId(managerId);
      const employeesList = allUsers.filter(user => {
        const userId = normalizeId(user.managerId);
        return userId === managerIdStr && user.role === 'employee';
      });
      setManagerEmployees(prev => ({
        ...prev,
        [managerIdStr]: employeesList
      }));
    } catch (error) {
      console.error('Error fetching employees for manager:', error);
    }
  };

  const handleCreateClient = () => {
    setEditingUser(null);
    setFixedRole('client');
    setSelectedClient(null);
    setSelectedManager(null);
    setShowModal(true);
  };

  const handleCreateManager = (client) => {
    setEditingUser(null);
    setFixedRole('manager');
    setSelectedClient(client);
    setSelectedManager(null);
    setShowModal(true);
  };

  const handleCreateEmployee = (client, manager) => {
    setEditingUser(null);
    setFixedRole('employee');
    setSelectedClient(client);
    setSelectedManager(manager || null);
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFixedRole(null);
    setSelectedClient(null);
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${BASE_URL}/api/users/${userId}`);
      handleSuccess('User deleted successfully');
      fetchClients();
      // Refresh managers if a client is expanded
      if (expandedClient) {
        fetchManagersAndEmployees(expandedClient);
      }
      // Refresh employees if a manager is expanded
      if (expandedManager) {
        fetchEmployeesForManager(expandedManager);
      }
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting user');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingUser(null);
    setFixedRole(null);
    setSelectedClient(null);
    setSelectedManager(null);
    fetchClients();
    // Refresh managers if a client is expanded
    if (expandedClient) {
      fetchManagersAndEmployees(expandedClient);
    }
    // Refresh employees if a manager is expanded
    if (expandedManager) {
      fetchEmployeesForManager(expandedManager);
    }
    if (onUpdate) onUpdate();
  };

  const handleSuccess = (message) => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    setNotification(message);
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification('');
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  const handleClientClick = (client) => {
    const clientId = normalizeId(client.id || client._id);
    if (expandedClient === clientId) {
      setExpandedClient(null);
      setManagers([]);
      setExpandedManager(null);
      setManagerEmployees({});
    } else {
      setExpandedClient(clientId);
      setExpandedManager(null);
      setManagerEmployees({});
      fetchManagersAndEmployees(clientId);
    }
  };

  const handleManagerClick = (manager) => {
    const managerId = normalizeId(manager.id || manager._id);
    if (expandedManager === managerId) {
      setExpandedManager(null);
      setManagerEmployees(prev => {
        const newState = { ...prev };
        delete newState[managerId];
        return newState;
      });
    } else {
      setExpandedManager(managerId);
      fetchEmployeesForManager(managerId);
    }
  };

  if (loading) return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '400px',
      fontSize: '1.125rem',
      color: '#64748b',
      fontWeight: '500'
    }}>
      Loading clients...
    </div>
  );

  return (
    <div className="user-management">
      <NotificationToast message={notification} />
      <div className="header-section">
        <h1>Client Management</h1>
        <button className="btn-primary" onClick={handleCreateClient}>
          + Add Client
        </button>
      </div>

      <div className="users-list">
        {clients.map(client => (
          <div key={client.id || client._id} className="user-card">
            <div className="user-card-header">
              <div className="user-info-inline">
                <h3>{client.name}</h3>
                <span className="user-username">@{client.username}</span>
              </div>
              <div className="user-actions">
                <button className="btn-view-details" onClick={() => handleClientClick(client)}>
                  {expandedClient === normalizeId(client.id || client._id) ? 'Hide Details' : 'View Details'}
                </button>
                <button className="btn-edit-client" onClick={() => handleEdit(client)}>Edit</button>
                <button className="btn-delete-client" onClick={() => handleDelete(client.id || client._id)}>Delete</button>
                <button className="btn-small" onClick={() => handleCreateManager(client)}>
                  + Add Manager
                </button>
              </div>
            </div>

            {expandedClient === normalizeId(client.id || client._id) && (
              <div className="hierarchy-section">
                <h4>Managers</h4>
                {managers.length > 0 ? (
                  managers.map(manager => {
                    const managerId = normalizeId(manager.id || manager._id);
                    const employeesForManager = managerEmployees[managerId] || [];
                    const isManagerExpanded = expandedManager === managerId;
                    
                    return (
                      <div key={manager.id || manager._id} className="manager-detail-card">
                        <div className="sub-user-card">
                          <div className="user-info-inline">
                            <span>{manager.name}</span>
                            <span className="user-username">@{manager.username}</span>
                          </div>
                          <div className="user-actions">
                            <button className="btn-view-details" onClick={() => handleManagerClick(manager)}>
                              {isManagerExpanded ? 'Hide Details' : 'View Details'}
                            </button>
                            <button className="btn-small" onClick={() => handleCreateEmployee(client, manager)}>
                              + Add Employee
                            </button>
                            <button className="btn-edit-client" onClick={() => handleEdit(manager)}>Edit</button>
                            <button className="btn-delete-client" onClick={() => handleDelete(manager.id || manager._id)}>Delete</button>
                          </div>
                        </div>
                        
                        {isManagerExpanded && (
                          <div className="employees-hierarchy-section">
                            <h5>Employees</h5>
                            {employeesForManager.length > 0 ? (
                              <div className="employees-list-nested">
                                {employeesForManager.map(employee => (
                                  <div key={employee.id || employee._id} className="employee-card-nested">
                                    <div className="user-info-inline">
                                      <strong>{employee.name}</strong>
                                      <span className="user-username">@{employee.username}</span>
                                    </div>
                                    <div className="user-actions">
                                      <button className="btn-edit-client" onClick={() => handleEdit(employee)}>Edit</button>
                                      <button className="btn-delete-client" onClick={() => handleDelete(employee.id || employee._id)}>Delete</button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="empty-message">No employees under this manager</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="empty-message">No managers assigned to this client yet</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {showModal && (
        <UserModal
          user={editingUser}
          fixedRole={fixedRole}
          selectedClient={selectedClient}
          selectedManager={selectedManager}
          onClose={handleModalClose}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default ClientManagement;

